import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@mui/material';
import _ from 'lodash';

import QES_CONSTANTS from 'generic/core/qes/constants';
import { fastGedOpen, saveDocument } from 'generic/core/ged/actions';
import { cleanupResultsComplete, fetchResultsComplete } from 'generic/core/search/actions';
import ResultsComplete from 'generic/components/pages/ResultsComplete';
import ResultsCompleteSkeleton from 'generic/components/skeletons/ResultsCompleteSkeleton';
import { toggleResultsCompleteVisibilityInResults } from 'generic/core/ux/actions';
import LoadingOverlay from 'generic/components/ui/LoadingOverlay';
import { getDocumentBaseId } from 'generic/utils/qesUtils';

const { ETAT_DOCUMENT, COMPLETE_ON_RESULTS_FULL_PAGE } = QES_CONSTANTS;

const ResultsCompleteContainer = ({ disableTagsRefine }) => {
  const bases = useSelector((state) => state.config.bases);
  const activeBaseId = useSelector((state) => state.config.activeBase.base);
  const urlApiSummarize = useSelector((state) => state.config.settings.urlApiSummarize_566);
  const loadingComplete = useSelector((state) => state.search.loadingComplete);
  const resultsComplete = useSelector((state) => state.search.resultsComplete);
  const resultsCompleteVisibleInResults = useSelector((state) => state.ux.resultsCompleteVisibleInResults);
  const isRAGSearch = useSelector((state) => state.search.isRAGSearch);
  const loadingResults = useSelector((state) => state.search.loading);
  const results = useSelector((state) => state.search.results);
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const smallerThanLarge = useMediaQuery(theme.breakpoints.down('lg'));

  const localIsAnnotatedTextDisplayed = JSON.parse(localStorage.getItem('isAnnotatedTextDisplayed'));
  const isAnnotateTextDisplayDefault = !_.isNil(localIsAnnotatedTextDisplayed) ? localIsAnnotatedTextDisplayed : true;
  const [isAnnotatedTextDisplayed, setIsAnnotatedTextDisplayed] = useState(isAnnotateTextDisplayDefault);
  const localIsTranslatedTextDisplayed = JSON.parse(localStorage.getItem('isTranslatedTextDisplayed'));
  const isTranslatedTextDisplayedDefault = !_.isNil(localIsTranslatedTextDisplayed) ? (
    localIsTranslatedTextDisplayed
  ) : false;
  const [isTranslatedTextDisplayed, setIsTranslatedTextDisplayed] = useState(isTranslatedTextDisplayedDefault);

  const handleToggleAnnotatedTextDisplay = (e) => {
    localStorage.setItem('isAnnotatedTextDisplayed', e.target.checked);
    setIsAnnotatedTextDisplayed(e.target.checked);
  };

  const handleToggleTranslatedTextDisplay = (e) => {
    localStorage.setItem('isTranslatedTextDisplayed', e.target.checked);
    setIsTranslatedTextDisplayed(e.target.checked);
  };

  const handleClose = () => {
    dispatch(cleanupResultsComplete());
  };

  const patchDocumentInComplete = (values) => {
    dispatch(saveDocument({
      article: resultsComplete.documents[0].idext,
      base: activeBaseId,
      patchInComplete: true,
      ...values,
    }));
  };

  useEffect(() => {
    if (COMPLETE_ON_RESULTS_FULL_PAGE
      && location.pathname.startsWith('/search/results')
      && !smallerThanLarge
      && !isRAGSearch
    ) {
      if (!resultsCompleteVisibleInResults) {
        dispatch(toggleResultsCompleteVisibilityInResults(true));
      }
      if (
        _.isEmpty(_.get(resultsComplete, 'documents', []))
        && !loadingComplete
        && !_.isEmpty(results.documents)
        && !loadingResults
      ) {
        dispatch(fetchResultsComplete(results.documents[0].idext));
      }
    } else if (resultsCompleteVisibleInResults) {
      dispatch(toggleResultsCompleteVisibilityInResults(false));
      handleClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsCompleteVisibleInResults, smallerThanLarge, location.pathname, isRAGSearch, loadingResults]);

  if (_.isEmpty(_.get(resultsComplete, 'documents', []))) {
    if (loadingComplete && !resultsCompleteVisibleInResults) {
      return <LoadingOverlay />;
    }

    if (resultsCompleteVisibleInResults && (loadingResults || !_.isEmpty(results.documents))) {
      return <ResultsCompleteSkeleton />;
    }

    return null;
  }

  const document = resultsComplete.documents[0];
  const documentBase = _.find(bases, { base: getDocumentBaseId(document) });

  const showButtons = _.isEmpty(matchPath(location.pathname, { path: ['/carts', '/newsletters'] }));

  return (
    <ResultsComplete
      canAddToCart={documentBase.panier}
      canAddToNewsletter={documentBase.newsletter}
      canComment={documentBase.commentaire}
      canDeleteDocument={_.find(resultsComplete.actions, { etat: ETAT_DOCUMENT.SUPPRIMER }) !== undefined}
      canEditInGED={documentBase.gedModification}
      canFastEditInGED={documentBase.avecGedRapide}
      canSendToFriend={documentBase.envoi}
      canSummarize={!_.isEmpty(urlApiSummarize)}
      disableTagsRefine={disableTagsRefine || activeBaseId !== document.base || isRAGSearch}
      handleClose={handleClose}
      handleToggleAnnotatedTextDisplay={handleToggleAnnotatedTextDisplay}
      isAnnotatedTextDisplayed={isAnnotatedTextDisplayed}
      handleToggleTranslatedTextDisplay={handleToggleTranslatedTextDisplay}
      isTranslatedTextDisplayed={isTranslatedTextDisplayed}
      loadingComplete={loadingComplete}
      openFastGed={() => dispatch(fastGedOpen(document.idext, document.base, 'result-complete'))}
      patchDocumentInComplete={patchDocumentInComplete}
      resultsComplete={resultsComplete}
      showButtons={showButtons}
      titleFieldName={documentBase.champTitre}
      variant={resultsCompleteVisibleInResults ? 'permanent' : 'temporary'}
      withMoreLikeThis={documentBase.avecMoreLikeThis}
    />
  );
};

ResultsCompleteContainer.propTypes = {
  disableTagsRefine: PropTypes.bool,
};

ResultsCompleteContainer.defaultProps = {
  disableTagsRefine: false,
};

export default ResultsCompleteContainer;
